// Unified Notifications page (lives under Settings).
// Two tabs: "Inbox" (listing of received notifications) + "Preferences" (channel matrix).
// Routes that hit /notifications redirect here.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import {
  Bell, Mail, Send, Save, Loader2, AlertTriangle, AlertCircle, Info,
  CheckCheck, Filter, Inbox, SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'account',  label: 'Account',  desc: 'Welcome, password resets, role changes.', defaultEmail: true,  defaultInApp: true },
  { key: 'project',  label: 'Project',  desc: 'Project memberships, archive events.',    defaultEmail: true,  defaultInApp: true },
  { key: 'raid',     label: 'RAID',     desc: 'Risk / Issue / Action assignments.',      defaultEmail: true,  defaultInApp: true },
  { key: 'schedule', label: 'Schedule', desc: 'CPM/PERT delays, milestone breaches.',    defaultEmail: false, defaultInApp: true },
  { key: 'cost',     label: 'Cost',     desc: 'CPI/SPI breaches, EAC overruns.',         defaultEmail: true,  defaultInApp: true },
  { key: 'hse',      label: 'HSE',      desc: 'Safety incidents, LTI alerts.',           defaultEmail: true,  defaultInApp: true },
  { key: 'mtm',      label: 'Material Tracking', desc: 'Auto-pilot stockout / over-commit alerts on live MTM inventory.', defaultEmail: true, defaultInApp: true },
  { key: 'security', label: 'Security', desc: 'Login alerts, suspicious activity.',      defaultEmail: false, defaultInApp: true },
  { key: 'system',   label: 'System',   desc: 'Integration outages, feature flags.',     defaultEmail: false, defaultInApp: true },
];

const SEV = {
  info:     { Icon: Info,          cls: 'text-[#5790C9]' },
  warn:     { Icon: AlertTriangle, cls: 'text-amber-400' },
  critical: { Icon: AlertCircle,   cls: 'text-red-400'   },
};

export default function SettingsNotifications() {
  const [search, setSearch] = useSearchParams();
  const initialTab = search.get('tab') === 'preferences' ? 'preferences' : 'inbox';
  const [tab, setTab] = useState(initialTab);
  const { projectId } = useProject();

  const switchTab = (t) => {
    setTab(t);
    setSearch(t === 'preferences' ? { tab: 'preferences' } : {}, { replace: true });
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5" style={{ color: '#5790C9' }}/>
            <h1 className="text-2xl font-light" style={{ color: 'var(--text-primary)' }}>Notifications</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Your inbox and delivery preferences — all under Settings.
          </p>
        </div>

        {/* AI Analyst Panel */}
        {projectId && (
          <div className="mb-5">
            <AIAnalystPanel projectId={projectId} moduleKey="NOTIFICATIONS_FEED" defaultOpen={false} compact={true} />
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex p-1 rounded-lg mb-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <TabButton active={tab === 'inbox'} onClick={() => switchTab('inbox')} icon={Inbox} label="Inbox" testId="notif-tab-inbox"/>
          <TabButton active={tab === 'preferences'} onClick={() => switchTab('preferences')} icon={SlidersHorizontal} label="Preferences" testId="notif-tab-prefs"/>
        </div>

        {tab === 'inbox' ? <InboxTab onJumpToPrefs={() => switchTab('preferences')}/> : <PreferencesTab/>}
      </div>
    </DashboardLayout>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs uppercase tracking-wider transition ${active ? 'bg-[#1E4E8C] text-white' : 'hover:bg-slate-800/40'}`}
      style={{ color: active ? '#fff' : 'var(--text-secondary)' }}>
      <Icon className="w-3.5 h-3.5"/> {label}
    </button>
  );
}

/* ---------------- Inbox tab ---------------- */
function InboxTab({ onJumpToPrefs }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [category, setCategory] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications', { params: { limit: 200 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = items.filter(n => {
    if (filter === 'unread' && n.read) return false;
    if (category && n.category !== category) return false;
    return true;
  });
  const categories = useMemo(() => Array.from(new Set(items.map(n => n.category))), [items]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems(arr => arr.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnread(c => Math.max(0, c - 1));
  };
  const markAll = async () => {
    await api.post('/notifications/read-all');
    setItems(arr => arr.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {items.length} total · <span style={{ color: '#F26B1F' }}>{unread} unread</span>
        </p>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={markAll} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded-md border" data-testid="notif-page-mark-all"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
              <CheckCheck className="w-3.5 h-3.5"/> Mark all read
            </button>
          )}
          <button onClick={onJumpToPrefs} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded-md bg-[#1E4E8C] hover:bg-[#143A6B] text-white" data-testid="notif-page-prefs">
            <SlidersHorizontal className="w-3.5 h-3.5"/> Preferences
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5 p-3 rounded-lg" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <Filter className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }}/>
        {[['all','All'],['unread','Unread']].map(([k,l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded text-xs transition ${filter===k ? 'bg-[#1E4E8C] text-white' : 'hover:bg-slate-800/40'}`} style={{ color: filter===k ? '#fff' : 'var(--text-secondary)' }}>
            {l}
          </button>
        ))}
        <span className="mx-2" style={{ color: 'var(--text-muted)' }}>·</span>
        <button onClick={() => setCategory('')} className={`px-3 py-1 rounded text-xs ${category==='' ? 'bg-slate-800/60 text-slate-100' : ''}`} style={{ color: category==='' ? undefined : 'var(--text-secondary)' }}>All categories</button>
        {categories.map(c => (
          <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1 rounded text-xs ${category===c ? 'bg-slate-800/60 text-slate-100' : ''}`} style={{ color: category===c ? undefined : 'var(--text-secondary)' }}>
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 rounded-xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <Bell className="w-12 h-12 mx-auto opacity-30 mb-4" style={{ color: 'var(--text-muted)' }}/>
          <h3 className="text-base mb-1" style={{ color: 'var(--text-primary)' }}>You're all caught up</h3>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications match your current filters.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map(n => {
            const sev = SEV[n.severity] || SEV.info;
            const SIcon = sev.Icon;
            return (
              <li key={n.notification_id} data-testid={`notif-row-${n.notification_id}`}
                onClick={() => { if (!n.read) markRead(n.notification_id); if (n.link) navigate(n.link.startsWith('/') ? n.link : `/${n.link}`); }}
                className="flex items-start gap-4 p-4 rounded-lg cursor-pointer transition hover:bg-slate-800/30"
                style={{ backgroundColor: n.read ? 'var(--card-bg)' : 'rgba(30,78,140,0.08)', border: `1px solid ${n.read ? 'var(--border-color)' : 'rgba(242,107,31,0.3)'}` }}>
                {!n.read && <span className="mt-2 w-2 h-2 rounded-full bg-[#F26B1F] shrink-0"/>}
                {n.read && <span className="mt-2 w-2 h-2 shrink-0"/>}
                <SIcon className={`w-5 h-5 shrink-0 mt-0.5 ${sev.cls}`}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-medium" style={{ color: 'var(--text-muted)' }}>{n.category}</span>
                    <span className="text-[10px] tracking-wider px-1.5 py-0.5 rounded uppercase border" style={{
                      color: sev.cls.includes('red') ? '#EF4444' : sev.cls.includes('amber') ? '#F59E0B' : '#5790C9',
                      borderColor: 'currentColor', opacity: 0.7,
                    }}>{n.severity}</span>
                  </div>
                  <div className={`text-sm mb-1 ${n.read ? '' : 'font-medium'}`} style={{ color: 'var(--text-primary)' }}>{n.title}</div>
                  <div className="text-xs whitespace-pre-line" style={{ color: 'var(--text-muted)' }}>{n.message}</div>
                  {n.actor_email && <div className="text-[10px] mt-2 font-mono" style={{ color: 'var(--text-disabled, #6B7280)' }}>By: {n.actor_email}</div>}
                </div>
                <span className="text-[10px] tabular-nums shrink-0" style={{ color: 'var(--text-muted)' }}>
                  {(n.created_at || '').slice(0, 10)}<br/>{(n.created_at || '').slice(11, 16)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/* ---------------- Preferences tab ---------------- */
function PreferencesTab() {
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get('/notifications/preferences'); setPrefs(r.data?.prefs || {}); }
      finally { setLoading(false); }
    })();
  }, []);

  const isOn = (cat, channel) => {
    const key = `${cat}_${channel}`;
    if (key in prefs) return prefs[key];
    const def = CATEGORIES.find(c => c.key === cat);
    return channel === 'email' ? def?.defaultEmail : def?.defaultInApp;
  };
  const setPref = (cat, channel, on) => setPrefs(p => ({ ...p, [`${cat}_${channel}`]: on }));

  const save = async () => {
    setSaving(true);
    try { await api.patch('/notifications/preferences', { prefs }); toast.success('Preferences saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      await api.post('/notifications/test', {
        event: 'system.feature_flag', title: 'Test notification from Settings',
        message: 'If you can see this, your in-app channel is working. Check your inbox for the email companion.',
        send_email: true,
      });
      toast.success('Test sent — check the bell + your inbox');
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed (admin-only)'); }
    finally { setSendingTest(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Choose which alerts you want to receive — by category and channel.</p>
        <div className="flex items-center gap-2">
          <button onClick={sendTest} disabled={sendingTest} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded-md border" data-testid="prefs-send-test"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>} Send test
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded-md bg-[#1E4E8C] hover:bg-[#143A6B] text-white" data-testid="prefs-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          <div className="grid grid-cols-[1fr_110px_110px] gap-x-4 px-5 py-3 text-[10px] tracking-[0.2em] uppercase font-medium" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
            <div>Category</div>
            <div className="flex items-center justify-center gap-1.5"><Bell className="w-3 h-3"/>In-App</div>
            <div className="flex items-center justify-center gap-1.5"><Mail className="w-3 h-3"/>Email</div>
          </div>
          {CATEGORIES.map((c) => (
            <div key={c.key} data-testid={`prefs-row-${c.key}`} className="grid grid-cols-[1fr_110px_110px] gap-x-4 px-5 py-4 items-center" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{c.label}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.desc}</div>
              </div>
              <div className="flex justify-center"><ToggleSwitch on={isOn(c.key, 'in_app')} onChange={(v) => setPref(c.key, 'in_app', v)} testId={`toggle-${c.key}-in_app`}/></div>
              <div className="flex justify-center"><ToggleSwitch on={isOn(c.key, 'email')} onChange={(v) => setPref(c.key, 'email', v)} testId={`toggle-${c.key}-email`}/></div>
            </div>
          ))}
          <div className="px-5 py-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
            Critical safety/security alerts may be delivered regardless of these preferences for compliance reasons.
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Toggle ---------------- */
function ToggleSwitch({ on, onChange, testId }) {
  return (
    <button
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
      data-testid={testId}
      className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5790C9] ${on ? 'bg-[#1E4E8C]' : 'bg-slate-700'}`}
    >
      <span
        className={`inline-block w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ease-out ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
      />
    </button>
  );
}
